import { FC } from "react";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useMemo, useEffect } from "react";
import Animated, {
  useSharedValue,
  withTiming,
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  useAnimatedReaction,
  withSpring,
} from "react-native-reanimated";
import { View } from "@src/components/View";
import { Platform, Pressable } from "react-native";
import { Icon, IconNameType } from "@src/components/Icon";
import { Text } from "@src/components/Text";
import { toast } from "@src/lib/sonner";
import { CustomToastProps } from "./types";
import { isWeb } from "@src/utils/web-helpers";

/**
 * CustomToast component that displays a toast with the given props.
 * @param id - The id of the toast.
 * @param type - The type of the toast.
 * @param message - The message to display in the toast.
 * @param description - The description to display in the toast.
 * @param actions - The actions to display in the toast.
 * @param closeButton - Whether to show a close button in the toast.
 * @param duration - The duration of the toast.
 * @param progress - Whether to show a progress bar in the toast.
 * @param dismissible - Whether the toast can be dismissed by swiping.
 * @example
 * <CustomToast
 *   id="1"
 *   type="success"
 *   message="Success"
 *   description="This is a success toast"
 *   actions={{ actionLabel: "Action", onAction: () => { console.log("Action"); } }}
 *   closeButton={false}
 *   duration={4000}
 *   progress={false}
 * />
 */
export const CustomToast: FC<CustomToastProps> = ({
  id,
  type,
  message,
  description,
  actions,
  closeButton = false,
  duration = 4000,
  progress = false,
  dismissible = true,
  containerStyle,
}) => {
  const { theme } = useUnistyles();

  const progressWidth = useSharedValue(100);
  const scale = useSharedValue(1);

  const showProgress = useMemo(() => {
    return progress && duration !== Infinity;
  }, [progress, duration]);

  const icon = useMemo(() => {
    if (type === "success")
      return {
        iconName: "checkmark",
        color: theme.colors.statusBadge.liveBorder,
      };
    if (type === "error")
      return {
        iconName: "warning",
        color: theme.colors.status.error,
      };
    if (type === "warning")
      return {
        iconName: "emergency",
        color: theme.colors.warning[500],
      };
    return {
      iconName: "info",
      color: theme.colors.interactive.buttons.default_background,
    };
  }, [type, theme]);

  const borderTopRightRadius = useDerivedValue(() => {
    return progressWidth.value === 100 ? 8 : 0;
  }, [progressWidth]);

  const animatedProgressBarStyle = useAnimatedStyle(() => ({
    width: `${progressWidth.value}%`,
    height: 6,
    backgroundColor: icon.color,
    borderTopRightRadius: borderTopRightRadius.value,
    borderTopLeftRadius: 8,
  }));

  const animatedToastStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  useEffect(() => {
    if (showProgress) {
      progressWidth.value = 100;
      progressWidth.value = withTiming(0, {
        duration: duration,
        easing: Easing.linear,
      });
    }
  }, [showProgress, duration, progressWidth]);

  const handleTouchStart = () => {
    if (!dismissible) {
      scale.value = withSpring(1.02, {
        damping: 30,
        stiffness: 600,
      });
    }
  };

  const handleTouchEnd = () => {
    if (!dismissible) {
      scale.value = withSpring(1, {
        damping: 30,
        stiffness: 600,
      });
    }
  };
  return (
    <Animated.View
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={[styles.toastContainer, animatedToastStyle, containerStyle]}
    >
      {/* Progress Bar */}
      {showProgress && (
        <View style={styles.progressBarContainer}>
          <Animated.View style={animatedProgressBarStyle} />
        </View>
      )}
      <View
        style={[
          styles.toast,
          {
            borderTopRightRadius: showProgress ? 0 : 8,
            borderTopLeftRadius: showProgress ? 0 : 8,
            borderTopWidth: showProgress ? 0 : 1,
          },
        ]}
      >
        {/* Close Button */}
        {closeButton && (
          <Pressable onPress={() => toast.dismiss(id)} style={styles.closeButton}>
            <Icon name="close" size={30} color={theme.colors.text} />
          </Pressable>
        )}
        {/* Toast Content */}
        <View p="4xl">
          <View flexDirection="row" gap="sm">
            {/* Type Icon */}
            <View>
              <Icon name={icon.iconName as IconNameType} size={20} color={icon.color} style={styles.icon} />
            </View>
            {/* Message and Description */}
            <View flex={1}>
              {/* Message */}
              <View style={styles.message}>
                <Text textStyle="sm/semibold">{message}</Text>
              </View>
              {/* Description */}
              {description && (
                <View style={styles.description}>
                  <Text textStyle="sm/regular" color="textMuted" style={{ lineHeight: 20 }}>
                    {description}
                  </Text>
                </View>
              )}
            </View>
          </View>
          {/* Actions */}
          {actions && (
            <View flexDirection="row" justifyContent="flex-end" alignItems="center" gap="lg" mt="2xl">
              {/* Action Button */}
              {actions.actionLabel && (
                <Pressable onPress={() => actions.onAction()} style={[styles.button, styles.actionButton]}>
                  <Text textStyle="sm/regular" style={styles.actionButtonText}>
                    {actions.actionLabel || "Action"}
                  </Text>
                </Pressable>
              )}
              {/* Cancel Button */}
              {actions.cancelLabel && (
                <Pressable onPress={() => actions.onCancel?.()} style={[styles.button, styles.cancelButton]}>
                  <Text textStyle="sm/regular" style={styles.cancelButtonText}>
                    {actions.cancelLabel || "Cancel"}
                  </Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create((theme) => ({
  toastContainer: {
    paddingHorizontal: isWeb ? 0 : theme.spacing["3xl"],
    minWidth: isWeb ? 380 : 0,
  },
  toast: {
    backgroundColor: theme.colors.surface1.background,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderBottomRightRadius: 8,
    borderBottomLeftRadius: 8,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: theme.colors.surface1.background,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: theme.colors.border,
    borderTopRightRadius: 8,
    borderTopLeftRadius: 8,
    zIndex: 1,
  },
  closeButton: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 1,
    opacity: 0.5,
  },
  icon: {
    top: -3,
  },
  message: {
    paddingRight: theme.spacing["4xl"],
  },
  description: {
    marginTop: theme.spacing.xs,
  },
  button: {
    height: 42,
    minWidth: 80,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: theme.spacing.xl,
    borderRadius: 8,
  },
  actionButton: {
    backgroundColor: theme.colors.interactive.buttons.default_background,
    borderWidth: 1,
    borderColor: theme.colors.surface1.background,
  },
  cancelButton: {
    backgroundColor: theme.colors.interactive.secondary.default_background,
    borderWidth: 1.5,
    borderColor: theme.colors.border,
  },
  actionButtonText: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
  cancelButtonText: {
    color: theme.colors.text,
  },
}));
